import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Search, Plus, Minus, Package2, Pencil, Check, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { EmptyState, RowSkeleton } from "../components/Skeletons";
import { useAuth } from "../contexts/AuthContext";

const STATUS_COLOR = {
  out: { bg: "bg-red-500/10", text: "text-red-600 dark:text-red-400", dot: "bg-red-500", label: "Out" },
  low: { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400", dot: "bg-amber-500", label: "Low" },
  healthy: { bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400", dot: "bg-emerald-500", label: "OK" },
};

function statusFromQty(q) {
  if (q === 0) return "out";
  if (q < 15) return "low";
  return "healthy";
}

function StockCell({ row, canAdjust, onAdjust }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(row.qty);
  const status = statusFromQty(row.qty);
  const sc = STATUS_COLOR[status];

  const commit = (e) => {
    e?.stopPropagation();
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0) {
      toast.error("Enter a non-negative number");
      setVal(row.qty);
      setEditing(false);
      return;
    }
    if (n !== row.qty) onAdjust(row, n - row.qty);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Input
          type="number"
          value={val}
          min={0}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit(e);
            if (e.key === "Escape") { setVal(row.qty); setEditing(false); }
          }}
          className="w-20 h-8 text-sm"
          autoFocus
          data-testid={`qty-input-${row.id}`}
        />
        <button onClick={commit} className="p-1 rounded text-emerald-600 hover:bg-emerald-500/10" data-testid={`qty-save-${row.id}`}>
          <Check size={14} />
        </button>
        <button onClick={() => { setVal(row.qty); setEditing(false); }} className="p-1 rounded text-muted-foreground hover:bg-foreground/5">
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => canAdjust && setEditing(true)}
        disabled={!canAdjust}
        className={`px-2 py-1 rounded-md min-w-[44px] tabular-nums text-sm font-semibold ${sc.bg} ${sc.text} ${canAdjust ? "hover:ring-1 hover:ring-[#534AB7]/30 cursor-pointer" : ""}`}
        data-testid={`qty-${row.id}`}
        title={canAdjust ? "Click to edit" : ""}
      >
        {row.qty}
      </button>
      {canAdjust && (
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAdjust(row, -1); }}
            className="p-1 rounded hover:bg-foreground/10 text-muted-foreground"
            data-testid={`dec-${row.id}`}
          >
            <Minus size={12} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAdjust(row, 1); }}
            className="p-1 rounded hover:bg-foreground/10 text-muted-foreground"
            data-testid={`inc-${row.id}`}
          >
            <Plus size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

export default function Inventory() {
  const { user } = useAuth();
  const canAdjust = ["admin", "manager", "officer"].includes(user?.role);
  const [tab, setTab] = useState("stock");
  const [inv, setInv] = useState([]);
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    const [i, m] = await Promise.all([api.get("/inventory"), api.get("/stock-movements")]);
    setInv(i.data);
    setMoves(m.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Optimistic update — the row updates instantly; rollback on error
  const adjust = async (row, delta) => {
    if (delta === 0) return;
    const prevQty = row.qty;
    const nextQty = Math.max(0, row.qty + delta);
    setInv((cur) => cur.map((x) => (x.id === row.id ? { ...x, qty: nextQty, status: statusFromQty(nextQty) } : x)));
    try {
      await api.post("/inventory/adjust", {
        product_id: row.product_id,
        item_id: row.item_id,
        size: row.size,
        delta,
        reason: "Manual adjustment",
      });
    } catch (e) {
      // rollback
      setInv((cur) => cur.map((x) => (x.id === row.id ? { ...x, qty: prevQty, status: statusFromQty(prevQty) } : x)));
      toast.error(formatApiError(e.response?.data?.detail) || "Could not adjust");
    }
  };

  // Group by item — produce one row per variant, with all sizes as columns
  const { rows, allSizes, totalsByStatus } = useMemo(() => {
    const grouped = {};
    inv.forEach((r) => {
      const key = r.item_id || "unknown";
      if (!grouped[key]) {
        grouped[key] = {
          key,
          item_name: r.item_name,
          product_name: r.product_name,
          sizes: {},
          total: 0,
        };
      }
      grouped[key].sizes[r.size] = r;
      grouped[key].total += r.qty;
    });
    const sizeOrder = ["XS", "S", "M", "L", "XL", "XXL", "Free", "6", "7", "8", "9", "10", "11", "Single", "Double", "Queen", "King", "50ml", "100ml", "200ml"];
    const seen = new Set();
    Object.values(grouped).forEach((g) => Object.keys(g.sizes).forEach((s) => seen.add(s)));
    const sizes = [...sizeOrder.filter((s) => seen.has(s)), ...Array.from(seen).filter((s) => !sizeOrder.includes(s))];

    const totals = { healthy: 0, low: 0, out: 0 };
    inv.forEach((r) => {
      totals[statusFromQty(r.qty)] += 1;
    });

    let rows = Object.values(grouped);
    if (q) {
      const ql = q.toLowerCase();
      rows = rows.filter((r) => (r.item_name + " " + r.product_name).toLowerCase().includes(ql));
    }
    if (statusFilter !== "all") {
      rows = rows.filter((r) => Object.values(r.sizes).some((s) => statusFromQty(s.qty) === statusFilter));
    }
    rows.sort((a, b) => a.item_name.localeCompare(b.item_name));
    return { rows, allSizes: sizes, totalsByStatus: totals };
  }, [inv, q, statusFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="inventory-title">
            Inventory & stock
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Click any quantity cell to edit. Hover for quick +/- buttons. Color tells you health at a glance.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="hidden md:flex items-center gap-3 mr-2">
            <span className="inline-flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full bg-emerald-500" /> {totalsByStatus.healthy} healthy
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full bg-amber-500" /> {totalsByStatus.low} low
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full bg-red-500" /> {totalsByStatus.out} out
            </span>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 k-glass" data-testid="inv-status-filter">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="out">Out of stock</SelectItem>
              <SelectItem value="low">Low stock</SelectItem>
              <SelectItem value="healthy">Healthy</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item..." className="pl-9 w-56 k-glass" data-testid="inv-search" />
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="k-glass">
          <TabsTrigger value="stock" data-testid="tab-stock">Stock levels</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">Movements</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "stock" ? (
        loading ? (
          <div className="k-glass rounded-2xl p-2">{Array.from({ length: 8 }).map((_, i) => <RowSkeleton key={i} cols={8} />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No inventory matches" description="Try clearing filters or search." icon={Package2} />
        ) : (
          <div className="k-glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto max-h-[68vh] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-card/95 backdrop-blur-md z-10">
                  <TableRow>
                    <TableHead className="w-[260px] sticky left-0 bg-card/95 backdrop-blur-md z-10">Item</TableHead>
                    {allSizes.map((s) => (
                      <TableHead key={s} className="text-center min-w-[110px]">{s}</TableHead>
                    ))}
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.key} data-testid={`inv-item-${r.key}`} className="group hover:bg-foreground/5 transition">
                      <TableCell className="sticky left-0 bg-background/95 group-hover:bg-card/95 backdrop-blur-md z-10">
                        <div className="font-semibold text-sm">{r.item_name}</div>
                        <div className="text-xs text-muted-foreground">{r.product_name}</div>
                      </TableCell>
                      {allSizes.map((s) => {
                        const row = r.sizes[s];
                        return (
                          <TableCell key={s} className="text-center">
                            {row ? (
                              <StockCell row={row} canAdjust={canAdjust} onAdjust={adjust} />
                            ) : (
                              <span className="text-muted-foreground/40">—</span>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right tabular-nums font-bold" style={{ fontFamily: "Outfit", color: "#534AB7" }}>
                        {r.total}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )
      ) : (
        <div className="k-glass rounded-2xl overflow-hidden">
          {loading ? (
            <div>{Array.from({ length: 5 }).map((_, i) => <RowSkeleton key={i} />)}</div>
          ) : moves.length === 0 ? (
            <EmptyState title="No stock movements yet" />
          ) : (
            <div className="max-h-[68vh] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-card/95 backdrop-blur-md z-10">
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Delta</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {moves.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="whitespace-nowrap">{m.created_at?.slice(0, 10)}</TableCell>
                      <TableCell>{m.item_name || "—"}</TableCell>
                      <TableCell>{m.size}</TableCell>
                      <TableCell><span className="capitalize text-xs px-2 py-0.5 rounded-full bg-foreground/5">{m.kind}</span></TableCell>
                      <TableCell className="text-muted-foreground text-sm">{m.reason || "—"}</TableCell>
                      <TableCell className={`text-right font-bold tabular-nums ${m.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {m.delta > 0 ? "+" : ""}{m.delta}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
